/**
 * Maps internal reaction values to the label set chosen by the workspace's
 * template `reaction_mode`. Internal values stay constant (`love`, `neutral`,
 * `not_me`) so analytics, exports, and historical reactions remain consistent
 * across plan changes — only the rendered label/icon swaps.
 */

export const REACTION_MODES = ['mood', 'simple', 'decision'];

// Three modes, three values each. Icons stay neutral (▲ ● ✕) for the
// non-mood modes so they read as decision affordances rather than mood
// signals.
const LABELS = {
  mood: {
    love:    { label: 'I feel this', shortLabel: 'I feel this', icon: '❤️' },
    neutral: { label: 'Maybe',       shortLabel: 'Maybe',       icon: '👍' },
    not_me:  { label: 'Not this',    shortLabel: 'Not this',    icon: '✕'  },
  },
  simple: {
    love:    { label: 'Yes',   shortLabel: 'Yes',   icon: '' },
    neutral: { label: 'Maybe', shortLabel: 'Maybe', icon: '' },
    not_me:  { label: 'No',    shortLabel: 'No',    icon: '' },
  },
  decision: {
    love:    { label: 'Agree',     shortLabel: 'Agree',     icon: '' },
    neutral: { label: 'Not agree', shortLabel: 'Not agree', icon: '' },
    not_me:  { label: 'Remove',    shortLabel: 'Remove',    icon: '' },
  },
};

// Map each (mode, value) to the i18n key the parent should pass in via
// the `t` (translation) map. Keeping this lookup in one place avoids
// scattering reaction-label strings across multiple files and lets the
// translation files remain the single source of truth for client-facing
// copy.
const I18N_KEYS = {
  mood: {
    love:    'reaction_love',
    neutral: 'reaction_neutral',
    not_me:  'reaction_not_me',
  },
  simple: {
    love:    'reaction_simple_yes',
    neutral: 'reaction_simple_maybe',
    not_me:  'reaction_simple_no',
  },
  decision: {
    love:    'reaction_decision_agree',
    neutral: 'reaction_decision_not_agree',
    not_me:  'reaction_decision_remove',
  },
};

const safeMode = (mode) => {
  const normalized = (mode || 'mood').toString().trim().toLowerCase();
  return REACTION_MODES.includes(normalized) ? normalized : 'mood';
};

export const getReactionLabel = (mode, value, t = null) => {
  const m = safeMode(mode);
  const entry = LABELS[m][value];
  if (!entry) return { label: '', icon: '', shortLabel: '' };
  // If a translation map was provided and contains the matching key,
  // override the EN defaults. We fall back per-key so partial
  // translations (e.g. only `reaction_love` localized) still work.
  if (t) {
    const key = I18N_KEYS[m]?.[value];
    const translated = key ? t[key] : null;
    if (translated) {
      return { ...entry, label: translated, shortLabel: translated };
    }
  }
  return entry;
};

/**
 * Returns the 3 selectable reactions in the canonical display order for a
 * given mode. Each item is `{ value, label, shortLabel, icon }`.
 */
export const getReactionButtons = (mode, t = null) => {
  const m = safeMode(mode);
  return ['love', 'neutral', 'not_me'].map((value) => ({
    value,
    ...getReactionLabel(m, value, t),
  }));
};

export const reactionSummaryLabel = (mode, value, t = null) => getReactionLabel(mode, value, t).shortLabel;
